import api from '../utils/api';

/**
 * @typedef {Object} ContentSection
 * @property {string} section
 * @property {Object} content
 */

/**
 * @typedef {Object} FAQ
 * @property {string} _id
 * @property {string} question
 * @property {string} answer
 * @property {string} category
 * @property {number} order
 */

/**
 * @typedef {Object} Slide
 * @property {string} _id
 * @property {string} title
 * @property {string} subtitle
 * @property {string} description
 * @property {string} image
 * @property {Object} cta
 * @property {string} cta.text
 * @property {string} cta.link
 * @property {boolean} isActive
 * @property {number} order
 * @property {number} clickCount
 * @property {number} viewCount
 */

/**
 * @typedef {Object} SocialLink
 * @property {string} platform
 * @property {string} url
 * @property {boolean} isActive
 * @property {number} displayOrder
 * @property {string} icon
 * @property {number} clicks
 */

export const settingsService = {
  // ==================
  // CONTENT MANAGEMENT
  // ==================

  /**
   * Get all content
   * @returns {Promise<{status: string, content: Object}>}
   */
  getContent: async () => {
    const response = await api.get('/api/content');
    return response;
  },

  /**
   * Get specific section
   * @param {string} section 
   * @returns {Promise<{status: string, content: ContentSection}>}
   */
  getSection: async (section) => {
    const response = await api.get(`/api/content/${section}`);
    return response;
  },

  /**
   * Get contact info
   * @returns {Promise<{status: string, contact: Object}>}
   */
  getContact: async () => {
    const response = await api.get('/api/content/public/contact');
    return response;
  },

  /**
   * Get FAQ
   * @returns {Promise<{status: string, faqs: FAQ[]}>}
   */
  getFAQ: async () => {
    const response = await api.get('/api/content/public/faq');
    return response;
  },

  /**
   * Update all content (Admin)
   * @param {Object} content 
   * @returns {Promise<{status: string, content: Object}>}
   */
  updateContent: async (content) => {
    const response = await api.put('/api/content', content);
    return response;
  },

  /**
   * Update specific section (Admin)
   * @param {string} section 
   * @param {Object} content 
   * @returns {Promise<{status: string, content: ContentSection}>}
   */
  updateSection: async (section, content) => {
    const response = await api.put(`/api/content/${section}`, content);
    return response;
  },

  // ==================
  // FAQ MANAGEMENT (Admin)
  // ==================

  /**
   * Add FAQ
   * @param {Object} faqData 
   * @returns {Promise<{status: string, faq: FAQ}>}
   */
  addFAQ: async (faqData) => {
    const response = await api.post('/api/content/faq', faqData);
    return response;
  },

  /**
   * Update FAQ
   * @param {string} faqId 
   * @param {Object} updates 
   * @returns {Promise<{status: string, faq: FAQ}>}
   */
  updateFAQ: async (faqId, updates) => {
    const response = await api.put(`/api/content/faq/${faqId}`, updates);
    return response;
  },

  /**
   * Delete FAQ
   * @param {string} faqId 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteFAQ: async (faqId) => {
    const response = await api.delete(`/api/content/faq/${faqId}`);
    return response;
  },

  /**
   * Toggle maintenance mode (Admin)
   * @returns {Promise<{status: string, maintenanceMode: boolean}>}
   */
  toggleMaintenanceMode: async () => {
    const response = await api.patch('/api/content/maintenance/toggle');
    return response;
  },

  // ==================
  // SLIDER MANAGEMENT
  // ==================

  /**
   * Get active slides
   * @returns {Promise<{status: string, slides: Slide[]}>}
   */
  getActiveSlides: async () => {
    const response = await api.get('/api/content/slider/active');
    return response;
  },

  /**
   * Track slide view
   * @param {string} slideId 
   * @returns {Promise<{status: string}>}
   */
  trackSlideView: async (slideId) => {
    try {
      const response = await api.post(`/api/content/slider/${slideId}/view`);
      return response;
    } catch (error) {
      // Fail silently for tracking
      return { status: 'error' };
    }
  },

  /**
   * Track slide click
   * @param {string} slideId 
   * @returns {Promise<{status: string}>}
   */
  trackSlideClick: async (slideId) => {
    try {
      const response = await api.post(`/api/content/slider/${slideId}/click`);
      return response;
    } catch (error) {
      // Fail silently for tracking
      return { status: 'error' };
    }
  },

  /**
   * Get all slides (Admin)
   * @returns {Promise<{status: string, slides: Slide[]}>}
   */
  getAllSlides: async () => {
    const response = await api.get('/api/content/slider/all');
    return response;
  },

  /**
   * Add slide (Admin)
   * @param {Object} slideData 
   * @returns {Promise<{status: string, slide: Slide}>}
   */
  addSlide: async (slideData) => {
    const response = await api.post('/api/content/slider', slideData);
    return response;
  },

  /**
   * Update slide (Admin)
   * @param {string} slideId 
   * @param {Object} updates 
   * @returns {Promise<{status: string, slide: Slide}>}
   */
  updateSlide: async (slideId, updates) => {
    const response = await api.put(`/api/content/slider/${slideId}`, updates);
    return response;
  },

  /**
   * Delete slide (Admin)
   * @param {string} slideId 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteSlide: async (slideId) => {
    const response = await api.delete(`/api/content/slider/${slideId}`);
    return response;
  },

  /**
   * Toggle slide status (Admin)
   * @param {string} slideId 
   * @returns {Promise<{status: string, slide: Slide}>}
   */
  toggleSlideStatus: async (slideId) => {
    const response = await api.patch(`/api/content/slider/${slideId}/toggle`);
    return response;
  },

  /**
   * Update slider settings (Admin)
   * @param {Object} settings 
   * @returns {Promise<{status: string, settings: Object}>}
   */
  updateSliderSettings: async (settings) => {
    const response = await api.put('/api/content/slider/settings', settings);
    return response;
  },

  /**
   * Reorder slides (Admin)
   * @param {Array<{slideId: string, order: number}>} slideOrders 
   * @returns {Promise<{status: string, slides: Slide[]}>}
   */
  reorderSlides: async (slideOrders) => {
    const response = await api.put('/api/content/slider/reorder', { slideOrders });
    return response;
  },

  // ==================
  // SOCIAL LINKS MANAGEMENT
  // ==================

  /**
   * Get all social links
   * @returns {Promise<{status: string, links: SocialLink[]}>}
   */
  getAllSocialLinks: async () => {
    const response = await api.get('/api/social-links');
    return response;
  },

  /**
   * Get social link
   * @param {string} platform 
   * @returns {Promise<{status: string, link: SocialLink}>}
   */
  getSocialLink: async (platform) => {
    const response = await api.get(`/api/social-links/${platform}`);
    return response;
  },

  /**
   * Track social link click
   * @param {string} platform 
   * @returns {Promise<{status: string}>}
   */
  trackSocialClick: async (platform) => {
    const response = await api.get(`/api/social-links/${platform}/click`);
    return response;
  },

  /**
   * Create social link (Admin)
   * @param {Object} linkData 
   * @returns {Promise<{status: string, link: SocialLink}>}
   */
  createSocialLink: async (linkData) => {
    const response = await api.post('/api/social-links', linkData);
    return response;
  },

  /**
   * Update social link (Admin)
   * @param {string} platform 
   * @param {Object} updates 
   * @returns {Promise<{status: string, link: SocialLink}>}
   */
  updateSocialLink: async (platform, updates) => {
    const response = await api.put(`/api/social-links/${platform}`, updates);
    return response;
  },

  /**
   * Delete social link (Admin)
   * @param {string} platform 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteSocialLink: async (platform) => {
    const response = await api.delete(`/api/social-links/${platform}`);
    return response;
  },

  /**
   * Get social links analytics (Admin)
   * @returns {Promise<{status: string, analytics: Object}>}
   */
  getSocialLinksAnalytics: async () => {
    const response = await api.get('/api/social-links/admin/analytics');
    return response;
  },
  // ==================
  // TESTIMONIALS MANAGEMENT
  // ==================

  /**
   * Add Testimonial
   * @param {Object} testimonialData 
   * @returns {Promise<{status: string, testimonial: Object}>}
   */
  addTestimonial: async (testimonialData) => {
    const response = await api.post('/api/content/testimonials', testimonialData);
    return response;
  },

  /**
   * Add Bulk Testimonials
   * @param {Array<Object>} testimonials 
   * @returns {Promise<{status: string, testimonials: Array<Object>}>}
   */
  addTestimonials: async (testimonials) => {
    const response = await api.post('/api/content/testimonials/bulk', { testimonials });
    return response;
  },

  /**
   * Update Testimonial
   * @param {string} testimonialId 
   * @param {Object} updates 
   * @returns {Promise<{status: string, testimonial: Object}>}
   */
  updateTestimonial: async (testimonialId, updates) => {
    const response = await api.put(`/api/content/testimonials/${testimonialId}`, updates);
    return response;
  },

  /**
   * Delete Testimonial
   * @param {string} testimonialId 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteTestimonial: async (testimonialId) => {
    const response = await api.delete(`/api/content/testimonials/${testimonialId}`);
    return response;
  },

  /**
   * Toggle Testimonial Status
   * @param {string} testimonialId 
   * @returns {Promise<{status: string, testimonial: Object}>}
   */
  toggleTestimonialStatus: async (testimonialId) => {
    const response = await api.patch(`/api/content/testimonials/${testimonialId}/toggle`);
    return response;
  },

  /**
   * Reorder Testimonials
   * @param {Array<{testimonialId: string, order: number}>} testimonialOrders 
   * @returns {Promise<{status: string, testimonials: Array<Object>}>}
   */
  reorderTestimonials: async (testimonialOrders) => {
    const response = await api.put('/api/content/testimonials/reorder', { testimonialOrders });
    return response;
  },
};
